import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  Form,
  Input,
  TextArea,
  Toast,
  DotLoading,
  ImageUploader,
  ProgressBar,
  Steps,
  Picker,
} from 'antd-mobile';
import type { ImageUploadItem } from 'antd-mobile/es/components/image-uploader';
import type { PickerValue } from 'antd-mobile/es/components/picker';
import { LeftOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CCI_AFTER_CHECKLIST_ITEMS,
  CCI_CHECKLIST_GROUPS,
  CCI_CHECKLIST_ITEMS,
  CCI_FITNESS_DECLARATION_TEXT_VER,
  FITNESS_QUESTION_IDS,
  compressImage,
  emptyFitnessAnswers,
  evaluateFitness,
  evaluateInspection,
  formatVehiclePublicLabel,
  generateUUID,
  isAfterChecklistComplete,
  itemsAnsweredCount,
  supabase,
  uploadFile,
  type BodyView,
  type ChecklistGroupId,
  type ChecklistItemDef,
  type ChecklistItemStatus,
  type FitnessAnswers,
  type InspectionBodyDefect,
  type InspectionItemState,
  type InspectionWorkFields,
  type EvaluateInspectionResult,
} from '@ubus/shared';
import { useAuth } from '../hooks/useAuth';
import {
  fetchTodayShiftInspection,
  inspectionTodayLocal,
  updateAfterFields,
} from '../utils/inspectionApi';
import BodyDiagram from '../components/BodyDiagram';

type ItemMap = Record<string, InspectionItemState>;

type TripVehicleOption = {
  id: string;
  label: string;
  pickup_address?: string | null;
  dropoff_address?: string | null;
};

type TripVehicleRow = {
  id: string;
  vehicle_id: string | null;
  pickup_time?: string | null;
  pickup_address?: string | null;
  dropoff_address?: string | null;
  vehicles?:
    | { fleet_number?: string; registration_plate?: string | null }
    | { fleet_number?: string; registration_plate?: string | null }[]
    | null;
};
type WizardStep = 0 | 1 | 2 | 3;

function extractShort(address: string): string {
  const part = address.split(',')[0]?.trim();
  return part && part.length > 0 ? part : address.slice(0, 28);
}

function emptyItems(defs: readonly ChecklistItemDef[]): ItemMap {
  const map: ItemMap = {};
  for (const def of defs) {
    map[def.id] = { id: def.id, status: 'unset' };
  }
  return map;
}

function fileExtension(file: File): string {
  const parts = file.name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'jpg';
}

function itemLabel(id: string, t: TFunction): string {
  return t(`inspection.items.${id}`, { defaultValue: id });
}

function firstInspectionReason(evaluation: EvaluateInspectionResult, t: TFunction): string | null {
  if (evaluation.incompleteItemIds.length) return t('inspection.reasonIncomplete');
  if (evaluation.blockingItemIds.length) return t('inspection.reasonCriticalFailed');
  if (evaluation.noteRequiredItemIds.length) return t('inspection.reasonNoteRequired');
  if (evaluation.reasons.some((r) => r.includes('Declaration'))) return t('inspection.reasonDeclaration');
  for (const reason of evaluation.reasons) {
    const naMatch = /^(.+): N\/A is not allowed$/.exec(reason);
    if (naMatch) {
      const item = CCI_CHECKLIST_ITEMS.find((i) => i.label === naMatch[1]);
      return t('inspection.reasonNaNotAllowed', {
        label: item ? itemLabel(item.id, t) : naMatch[1],
      });
    }
  }
  return evaluation.reasons[0] ?? null;
}

function ChecklistRows({
  defs,
  items,
  onStatus,
  onNote,
  soft,
  t,
}: {
  defs: readonly ChecklistItemDef[];
  items: ItemMap;
  onStatus: (id: string, status: ChecklistItemStatus) => void;
  onNote: (id: string, note: string) => void;
  soft?: boolean;
  t: TFunction;
}) {
  return (
    <>
      {defs.map((def) => {
        const state = items[def.id];
        const answered = state?.status && state.status !== 'unset';
        return (
          <div
            key={def.id}
            style={{
              border: answered ? '1px solid #b7eb8f' : '1px solid #e5e5e5',
              borderRadius: 10,
              padding: 12,
              marginBottom: 10,
              background: '#fff',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
              {itemLabel(def.id, t)}
              {!soft && def.severity === 'critical' && (
                <span style={{ color: '#c0392b', marginLeft: 6, fontSize: 12 }}>{t('inspection.critical')}</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(['pass', 'fail', ...(def.allowNa ? (['na'] as const) : [])] as ChecklistItemStatus[]).map((s) => (
                <Button
                  key={s}
                  size="mini"
                  color={state?.status === s ? (s === 'fail' ? 'danger' : 'primary') : 'default'}
                  fill={state?.status === s ? 'solid' : 'outline'}
                  onClick={() => onStatus(def.id, s)}
                >
                  {s === 'pass' ? t('inspection.pass') : s === 'fail' ? t('inspection.fail') : t('inspection.na')}
                </Button>
              ))}
            </div>
            {state?.status === 'fail' && (
              <TextArea
                style={{ marginTop: 8 }}
                placeholder={soft ? t('inspection.noteOptional') : t('inspection.note')}
                value={state.note || ''}
                onChange={(v) => onNote(def.id, v)}
                rows={2}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function GroupedChecklist({
  items,
  onStatus,
  onNote,
  onMarkGroupPass,
  t,
}: {
  items: ItemMap;
  onStatus: (id: string, status: ChecklistItemStatus) => void;
  onNote: (id: string, note: string) => void;
  onMarkGroupPass: (group: ChecklistGroupId) => void;
  t: TFunction;
}) {
  return (
    <>
      {CCI_CHECKLIST_GROUPS.map((g) => {
        const defs = CCI_CHECKLIST_ITEMS.filter((i) => i.group === g.id);
        const done = defs.filter((d) => items[d.id]?.status && items[d.id].status !== 'unset').length;
        return (
          <div key={g.id} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>
                {t(`inspection.groups.${g.id}`)}{' '}
                <span style={{ color: '#888', fontWeight: 400, fontSize: 12 }}>
                  {done}/{defs.length}
                </span>
              </h3>
              <Button size="mini" fill="outline" onClick={() => onMarkGroupPass(g.id)}>
                {t('inspection.allPass')}
              </Button>
            </div>
            <ChecklistRows defs={defs} items={items} onStatus={onStatus} onNote={onNote} t={t} />
          </div>
        );
      })}
    </>
  );
}

function PreTripInspection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return') || '/';
  const mode = searchParams.get('mode') === 'after' ? 'after' : 'pre';
  const { driverId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<WizardStep>(0);
  const [items, setItems] = useState<ItemMap>(() => emptyItems(CCI_CHECKLIST_ITEMS));
  const [afterItems, setAfterItems] = useState<ItemMap>(() => emptyItems(CCI_AFTER_CHECKLIST_ITEMS));
  const [defects, setDefects] = useState<InspectionBodyDefect[]>([]);
  const [defectPhotos, setDefectPhotos] = useState<Record<string, ImageUploadItem[]>>({});
  const [declared, setDeclared] = useState(false);
  const [visualConfirm, setVisualConfirm] = useState(false);
  const [fitness, setFitness] = useState<FitnessAnswers>(() => emptyFitnessAnswers());
  const [fitnessSigned, setFitnessSigned] = useState(false);
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [vehicleOptions, setVehicleOptions] = useState<TripVehicleOption[]>([]);
  const [vehiclePickerVisible, setVehiclePickerVisible] = useState(false);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [afterNote, setAfterNote] = useState('');
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [work, setWork] = useState<InspectionWorkFields>({
    date: inspectionTodayLocal(),
    driver_name: '',
    rego: '',
  });

  useEffect(() => {
    if (!driverId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      const today = inspectionTodayLocal();

      const existing = await fetchTodayShiftInspection(driverId);
      if (cancelled) return;
      if (existing) {
        setExistingId(existing.id);
        setWork((prev) => ({
          ...prev,
          ...existing.work_fields,
          date: existing.work_fields?.date || today,
        }));
        const existingFitness = existing.work_fields?.fitness;
        if (existingFitness) {
          setFitness({
            ...emptyFitnessAnswers(),
            ...existingFitness,
            answers: { ...emptyFitnessAnswers().answers, ...existingFitness.answers },
          });
          setFitnessSigned(!!(existingFitness.signed_name && existingFitness.signed_at));
        }
        if (existing.after_items?.length) {
          const map = emptyItems(CCI_AFTER_CHECKLIST_ITEMS);
          for (const row of existing.after_items) {
            map[row.id] = { id: row.id, status: row.status, note: row.note };
          }
          setAfterItems(map);
        }
      }

      if (mode === 'after') {
        setLoading(false);
        return;
      }

      const { data: driverRow } = await supabase
        .from('drivers')
        .select('id, name')
        .eq('id', driverId)
        .maybeSingle();

      const { data: trips } = await supabase
        .from('trips')
        .select(
          'id, vehicle_id, pickup_time, pickup_address, dropoff_address, vehicles(fleet_number, registration_plate)'
        )
        .eq('driver_id', driverId)
        .eq('date', today)
        .in('status', ['dispatched', 'scheduled', 'in_progress'])
        .not('vehicle_id', 'is', null)
        .order('pickup_time', { ascending: true });

      if (cancelled) return;

      const seen = new Set<string>();
      const options: TripVehicleOption[] = [];
      for (const trip of (trips ?? []) as TripVehicleRow[]) {
        if (!trip.vehicle_id || seen.has(trip.vehicle_id)) continue;
        seen.add(trip.vehicle_id);
        const v = Array.isArray(trip.vehicles) ? trip.vehicles[0] : trip.vehicles;
        const label = formatVehiclePublicLabel({
          fleet_number: v?.fleet_number || '',
          registration_plate: v?.registration_plate,
        });
        if (!label.trim()) continue;
        options.push({
          id: trip.vehicle_id,
          label,
          pickup_address: trip.pickup_address,
          dropoff_address: trip.dropoff_address,
        });
      }

      setVehicleOptions(options);

      const existingVehicleId = existing?.vehicle_id ?? null;
      const preferred =
        (existingVehicleId && options.find((o) => o.id === existingVehicleId)) ||
        options[0] ||
        null;

      setVehicleId(preferred?.id ?? null);
      setWork((prev) => ({
        ...prev,
        date: today,
        driver_name: driverRow?.name || prev.driver_name || '',
        rego: preferred?.label || (options.length ? '' : prev.rego) || '',
        pick_up: preferred?.pickup_address || prev.pick_up,
        drop_off: preferred?.dropoff_address || prev.drop_off,
      }));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [driverId, mode]);

  const evaluation = useMemo(
    () =>
      evaluateInspection({
        items: Object.values(items),
        declarationAccepted: declared && visualConfirm,
      }),
    [items, declared, visualConfirm]
  );

  const fitnessEval = useMemo(
    () =>
      evaluateFitness(
        {
          ...fitness,
          signed_name: fitnessSigned ? work.driver_name || fitness.signed_name || 'Driver' : undefined,
          signed_at: fitnessSigned ? fitness.signed_at || new Date().toISOString() : undefined,
        },
        { requireSigned: true }
      ),
    [fitness, fitnessSigned, work.driver_name]
  );

  const canLeaveStep2 = fitnessEval.allYes && fitnessSigned;

  const answered = itemsAnsweredCount(Object.values(items));
  const totalItems = CCI_CHECKLIST_ITEMS.length;
  const afterComplete = useMemo(
    () => isAfterChecklistComplete(Object.values(afterItems)),
    [afterItems]
  );

  const setStatus = (id: string, status: ChecklistItemStatus) => {
    setItems((prev) => ({
      ...prev,
      [id]: { ...prev[id], id, status, note: status === 'fail' ? prev[id]?.note : undefined },
    }));
  };

  const setNote = (id: string, note: string) => {
    setItems((prev) => ({
      ...prev,
      [id]: { ...prev[id], id, status: prev[id]?.status ?? 'fail', note },
    }));
  };

  const markGroupPass = (group: ChecklistGroupId) => {
    setItems((prev) => {
      const next = { ...prev };
      for (const def of CCI_CHECKLIST_ITEMS.filter((i) => i.group === group)) {
        next[def.id] = { id: def.id, status: 'pass' };
      }
      return next;
    });
  };

  const setAfterStatus = (id: string, status: ChecklistItemStatus) => {
    setAfterItems((prev) => ({
      ...prev,
      [id]: { ...prev[id], id, status, note: status === 'fail' ? prev[id]?.note : undefined },
    }));
  };

  const setAfterItemNote = (id: string, note: string) => {
    setAfterItems((prev) => ({
      ...prev,
      [id]: { ...prev[id], id, status: prev[id]?.status ?? 'fail', note },
    }));
  };

  const toggleDefect = (view: BodyView) => {
    setDefects((prev) => {
      const exists = prev.find((d) => d.view === view);
      if (exists) {
        setDefectPhotos((p) => {
          const next = { ...p };
          delete next[view];
          return next;
        });
        return prev.filter((d) => d.view !== view);
      }
      return [...prev, { view, note: '' }];
    });
  };

  const setDefectNote = (view: BodyView, note: string) => {
    setDefects((prev) => prev.map((d) => (d.view === view ? { ...d, note } : d)));
  };

  const uploadDefectPhoto = async (file: File): Promise<ImageUploadItem> => {
    return { url: URL.createObjectURL(file), extra: file };
  };

  const resolveDefectPhotoUrls = async (
    current: InspectionBodyDefect[]
  ): Promise<{ defects: InspectionBodyDefect[]; photoUrls: string[] }> => {
    const next: InspectionBodyDefect[] = [];
    const photoUrls: string[] = [];

    for (const d of current) {
      const fileItems = defectPhotos[d.view] || [];
      const file = fileItems[0]?.extra instanceof File ? fileItems[0].extra : null;
      if (!file) {
        next.push(d);
        if (d.photo_url) photoUrls.push(d.photo_url);
        continue;
      }
      try {
        const compressed = await compressImage(file);
        const path = await uploadFile(
          'uploads',
          `inspections/${generateUUID()}.${fileExtension(compressed)}`,
          compressed
        );
        if (path) {
          next.push({ ...d, photo_url: path });
          photoUrls.push(path);
        } else {
          next.push(d);
        }
      } catch {
        next.push(d);
      }
    }
    return { defects: next, photoUrls };
  };

  const selectVehicle = (id: string | null) => {
    const opt = vehicleOptions.find((o) => o.id === id) ?? null;
    setVehicleId(opt?.id ?? null);
    setWork((prev) => ({
      ...prev,
      rego: opt?.label || '',
      pick_up: opt?.pickup_address || prev.pick_up,
      drop_off: opt?.dropoff_address || prev.drop_off,
    }));
  };

  const canLeaveStep0 = !!(vehicleId && work.rego?.trim() && work.start_km?.trim());
  const canLeaveStep1 = answered === totalItems;

  const handleAfterSubmit = async () => {
    if (!existingId) {
      Toast.show({ icon: 'fail', content: t('inspection.noInspectionToday') });
      return;
    }
    setSubmitting(true);
    const result = await updateAfterFields(existingId, {
      workFields: {
        finish_km: work.finish_km,
        depot_finish_time: work.depot_finish_time,
      },
      afterItems: Object.values(afterItems)
        .filter((i) => i.status !== 'unset')
        .map((i) => ({
          id: i.id,
          status: i.status as 'pass' | 'fail' | 'na',
          note: i.note,
        })),
      afterNote: afterNote || undefined,
    });
    setSubmitting(false);
    if (!result.ok) {
      Toast.show({ icon: 'fail', content: t('inspection.afterSaveFailed') });
      return;
    }
    Toast.show({
      icon: 'success',
      content: result.complete ? t('inspection.afterCompleted') : t('inspection.afterSavedIncomplete'),
    });
    navigate(returnTo, { replace: true });
  };

  const handleSubmit = async () => {
    if (!driverId) return;
    const evalResult = evaluateInspection({
      items: Object.values(items),
      declarationAccepted: declared && visualConfirm,
    });
    if (!evalResult.canDeclare) {
      Toast.show({ icon: 'fail', content: evalResult.reasons[0] ? firstInspectionReason(evalResult, t) : t('inspection.cannotSubmit') });
      return;
    }

    setSubmitting(true);
    const today = inspectionTodayLocal();
    if (!vehicleId) {
      setSubmitting(false);
      Toast.show({ icon: 'fail', content: t('inspection.vehicleRequired') });
      return;
    }
    const itemRows = Object.values(items).map((i) => ({
      id: i.id,
      status: i.status === 'unset' ? 'pass' : i.status,
      note: i.note,
    }));
    const checked = itemRows.filter((i) => i.status === 'pass' || i.status === 'na').map((i) => i.id);
    const { defects: defectsWithPhotos, photoUrls: photo_urls } = await resolveDefectPhotoUrls(defects);

    const fitnessPayload: FitnessAnswers = {
      ...fitness,
      form_ver: fitness.form_ver,
      signed_name: (work.driver_name || '').trim() || 'Driver',
      signed_at: new Date().toISOString(),
    };
    if (!evaluateFitness(fitnessPayload, { requireSigned: true }).canDeclare) {
      setSubmitting(false);
      Toast.show({ icon: 'fail', content: t('inspection.fitnessRequired') });
      return;
    }

    const { error } = await supabase.from('vehicle_inspections').insert({
      driver_id: driverId,
      vehicle_id: vehicleId,
      inspection_type: 'pre-trip',
      inspection_date: today,
      checked_items: checked,
      items: itemRows,
      body_defects: defectsWithPhotos,
      work_fields: { ...work, fitness: fitnessPayload },
      declaration_accepted_at: new Date().toISOString(),
      declaration_text_ver: CCI_FITNESS_DECLARATION_TEXT_VER,
      severity_result: evalResult.severityResult,
      photo_urls,
      after_items: [],
      confirmed_trip_ids: [],
      notes: defectsWithPhotos.map((d) => `${d.view}: ${d.note || 'marked'}`).join('; ') || null,
    });

    setSubmitting(false);

    if (error) {
      Toast.show({ icon: 'fail', content: error.message });
      return;
    }

    Toast.show({ icon: 'success', content: t('inspection.done') });
    navigate(returnTo, { replace: true });
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <DotLoading color="primary" />
      </div>
    );
  }

  if (mode === 'after') {
    return (
      <div className="driver-page" style={{ paddingBottom: 96 }}>
        <div className="driver-detail-header" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px' }}>
          <button type="button" aria-label={t('common.back')} onClick={() => navigate(-1)} style={{ border: 0, background: 'transparent' }}>
            <LeftOutline fontSize={22} />
          </button>
          <h1 style={{ margin: 0, fontSize: 18 }}>{t('inspection.finishCheck')}</h1>
        </div>
        <section style={{ padding: '0 16px 16px' }}>
          {!existingId && (
            <p style={{ color: '#c0392b', fontSize: 13 }}>{t('inspection.noMorningCheck')}</p>
          )}
          <Form layout="vertical">
            <Form.Item label={t('inspection.finishKm')}>
              <Input value={work.finish_km} onChange={(v) => setWork((w) => ({ ...w, finish_km: v }))} />
            </Form.Item>
            <Form.Item label={t('inspection.depotFinish')}>
              <Input
                value={work.depot_finish_time}
                onChange={(v) => setWork((w) => ({ ...w, depot_finish_time: v }))}
                placeholder={t('inspection.timePlaceholder')}
              />
            </Form.Item>
          </Form>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <Button
              size="mini"
              onClick={() => {
                setAfterItems(emptyItems(CCI_AFTER_CHECKLIST_ITEMS));
                setAfterItems((prev) => {
                  const next = { ...prev };
                  for (const def of CCI_AFTER_CHECKLIST_ITEMS) {
                    next[def.id] = { id: def.id, status: 'pass' };
                  }
                  return next;
                });
              }}
            >
              {t('inspection.markAllPass')}
            </Button>
          </div>
          <ChecklistRows
            defs={CCI_AFTER_CHECKLIST_ITEMS}
            items={afterItems}
            onStatus={setAfterStatus}
            onNote={setAfterItemNote}
            soft
            t={t}
          />
          <Form layout="vertical">
            <Form.Item label={t('inspection.extraNotes')}>
              <TextArea value={afterNote} onChange={setAfterNote} rows={3} />
            </Form.Item>
          </Form>
          <Button
            block
            color="primary"
            size="large"
            loading={submitting}
            disabled={!existingId || submitting}
            onClick={() => {
              void handleAfterSubmit();
            }}
          >
            {afterComplete ? t('inspection.done') : t('common.save')}
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="driver-page" style={{ paddingBottom: 110 }}>
      <div className="driver-detail-header" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px' }}>
        <button type="button" aria-label={t('common.back')} onClick={() => navigate(-1)} style={{ border: 0, background: 'transparent' }}>
          <LeftOutline fontSize={22} />
        </button>
        <h1 style={{ margin: 0, fontSize: 18 }}>{t('inspection.vehicleCheck')}</h1>
      </div>

      <div style={{ padding: '0 16px 12px' }}>
        <Steps current={step}>
          <Steps.Step title={t('inspection.stepVehicle')} />
          <Steps.Step title={t('inspection.stepChecks')} />
          <Steps.Step title={t('inspection.stepFitness')} />
          <Steps.Step title={t('inspection.stepDone')} />
        </Steps>
      </div>

      {step === 0 && (
        <section style={{ padding: '0 16px 16px' }}>
          {(work.driver_name || work.pick_up) && (
            <div style={{ fontSize: 13, color: '#888', marginBottom: 12, lineHeight: 1.4 }}>
              {[work.driver_name, work.date, work.pick_up ? `→ ${extractShort(work.pick_up)}` : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
          <Form layout="vertical">
            <Form.Item label={t('inspection.rego')} required>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (vehicleOptions.length === 0) return;
                  setVehiclePickerVisible(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (vehicleOptions.length === 0) return;
                    setVehiclePickerVisible(true);
                  }
                }}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: '#f5f5f5',
                  color: work.rego ? '#111' : '#999',
                  fontSize: 16,
                }}
              >
                {work.rego ||
                  (vehicleOptions.length === 0
                    ? t('inspection.noVehicleForToday')
                    : t('inspection.regoSelectPlaceholder'))}
              </div>
              <Picker
                columns={[
                  vehicleOptions.map((o) => ({
                    label: o.label,
                    value: o.id,
                  })),
                ]}
                visible={vehiclePickerVisible}
                onClose={() => setVehiclePickerVisible(false)}
                value={vehicleId ? [vehicleId] : []}
                onConfirm={(vals: PickerValue[]) => {
                  const next = typeof vals[0] === 'string' ? vals[0] : null;
                  selectVehicle(next);
                  setVehiclePickerVisible(false);
                }}
              />
            </Form.Item>
            {vehicleOptions.length === 0 ? (
              <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 12 }}>
                {t('inspection.noVehicleForTodayHint')}
              </div>
            ) : null}
            <Form.Item label={t('inspection.startKm')} required>
              <Input value={work.start_km} onChange={(v) => setWork((w) => ({ ...w, start_km: v }))} placeholder={t('inspection.startKmPlaceholder')} />
            </Form.Item>
          </Form>
          <button
            type="button"
            onClick={() => setShowMoreFields((v) => !v)}
            style={{ border: 0, background: 'transparent', color: '#1677ff', fontSize: 14, padding: '4px 0 12px' }}
          >
            {showMoreFields ? t('inspection.hideMore') : t('inspection.moreFields')}
          </button>
          {showMoreFields && (
            <Form layout="vertical">
              <Form.Item label={t('inspection.tour')}>
                <Input value={work.tour_name} onChange={(v) => setWork((w) => ({ ...w, tour_name: v }))} />
              </Form.Item>
              <Form.Item label={t('inspection.hotel')}>
                <Input value={work.hotel_name} onChange={(v) => setWork((w) => ({ ...w, hotel_name: v }))} />
              </Form.Item>
              <Form.Item label={t('inspection.depotStart')}>
                <Input value={work.depot_start_time} onChange={(v) => setWork((w) => ({ ...w, depot_start_time: v }))} placeholder={t('inspection.timePlaceholder')} />
              </Form.Item>
              <Form.Item label={t('inspection.fuel')}>
                <Input value={work.fuel_level} onChange={(v) => setWork((w) => ({ ...w, fuel_level: v }))} />
              </Form.Item>
            </Form>
          )}
          {!canLeaveStep0 && (
            <div style={{ color: '#c0392b', fontSize: 13 }}>
              {vehicleOptions.length === 0
                ? t('inspection.noVehicleForTodayHint')
                : t('inspection.regoStartRequired')}
            </div>
          )}
        </section>
      )}

      {step === 1 && (
        <section style={{ padding: '0 16px 16px' }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span>{answered}/{totalItems}</span>
            </div>
            <ProgressBar percent={Math.round((answered / totalItems) * 100)} />
          </div>
          <GroupedChecklist
            items={items}
            onStatus={setStatus}
            onNote={setNote}
            onMarkGroupPass={markGroupPass}
            t={t}
          />
        </section>
      )}

      {step === 2 && (
        <section style={{ padding: '0 16px 16px' }}>
          <h2 style={{ fontSize: 15, marginBottom: 6 }}>{t('inspection.fitnessTitle')}</h2>
          <p style={{ fontSize: 12, color: '#888', lineHeight: 1.4, marginTop: 0 }}>
            {t('inspection.fitnessIntro')}
          </p>
          {FITNESS_QUESTION_IDS.map((qid) => {
            const value = fitness.answers[qid];
            return (
              <div
                key={qid}
                style={{
                  marginBottom: 12,
                  padding: 10,
                  border: '1px solid #eee',
                  borderRadius: 8,
                }}
              >
                <div style={{ fontSize: 14, marginBottom: 8, lineHeight: 1.35 }}>
                  {t(`inspection.fitnessQuestions.${qid}`)}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {([true, false] as const).map((yes) => {
                    const selected = value === yes;
                    return (
                      <Button
                        key={String(yes)}
                        size="small"
                        color={selected ? (yes ? 'primary' : 'danger') : 'default'}
                        fill={selected ? 'solid' : 'outline'}
                        onClick={() =>
                          setFitness((prev) => ({
                            ...prev,
                            answers: { ...prev.answers, [qid]: yes },
                          }))
                        }
                      >
                        {yes ? t('common.yes') : t('common.no')}
                      </Button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {fitnessEval.failingIds.length > 0 && (
            <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 10 }}>
              {t('inspection.fitnessFailHint')}
            </div>
          )}
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {t('inspection.fitnessName')}: <strong>{work.driver_name || '—'}</strong>
            {' · '}
            {t('inspection.fitnessDate')}: <strong>{work.date || inspectionTodayLocal()}</strong>
          </div>
          <Checkbox
            checked={fitnessSigned}
            disabled={!fitnessEval.allYes}
            onChange={(v) => {
              setFitnessSigned(v);
              if (v) {
                setFitness((prev) => ({
                  ...prev,
                  signed_name: work.driver_name || prev.signed_name,
                  signed_at: new Date().toISOString(),
                }));
              }
            }}
          >
            {t('inspection.fitnessSignCheckbox')}
          </Checkbox>
          <p style={{ fontSize: 12, color: '#666', marginTop: 12, lineHeight: 1.4 }}>
            {t('inspection.fitnessWarning')}
          </p>
        </section>
      )}

      {step === 3 && (
        <section style={{ padding: '0 16px 16px' }}>
          <h2 style={{ fontSize: 15, marginBottom: 8 }}>{t('inspection.body')}</h2>
          <BodyDiagram
            selected={defects.map((d) => d.view)}
            onToggle={toggleDefect}
          />
          {defects.map((d) => (
            <div key={d.view} style={{ marginTop: 12, padding: 10, border: '1px solid #eee', borderRadius: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(`inspection.bodyViews.${d.view}`)}</div>
              <TextArea
                placeholder={t('inspection.noteOptional')}
                value={d.note || ''}
                onChange={(v) => setDefectNote(d.view, v)}
                rows={2}
              />
              <div style={{ marginTop: 8 }}>
                <ImageUploader
                  value={defectPhotos[d.view] || []}
                  onChange={(list) => setDefectPhotos((prev) => ({ ...prev, [d.view]: list }))}
                  upload={async (file) => uploadDefectPhoto(file)}
                  maxCount={1}
                  accept="image/jpeg,image/png,image/jpg"
                />
              </div>
            </div>
          ))}

          <h2 style={{ fontSize: 15, margin: '20px 0 8px' }}>{t('inspection.confirm')}</h2>
          <p style={{ fontSize: 12, lineHeight: 1.4, color: '#888', marginTop: 0 }}>
            {t('inspection.fitForDuty')}
          </p>
          <Checkbox
            checked={declared && visualConfirm}
            onChange={(v) => {
              setDeclared(v);
              setVisualConfirm(v);
            }}
            style={{ marginTop: 8 }}
          >
            {t('inspection.confirmCheckbox')}
          </Checkbox>
          {evaluation.reasons.length > 0 && (
            <div style={{ marginTop: 12, color: '#c0392b', fontSize: 13 }}>
              {firstInspectionReason(evaluation, t)}
            </div>
          )}
        </section>
      )}

      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '10px 16px 16px',
          background: '#fff',
          borderTop: '1px solid #eee',
          display: 'flex',
          gap: 8,
          zIndex: 20,
        }}
      >
        {step > 0 && (
          <Button
            block
            fill="outline"
            onClick={() => setStep((s) => (s - 1) as WizardStep)}
          >
            {t('common.back')}
          </Button>
        )}
        {step < 3 && (
          <Button
            block
            color="primary"
            disabled={
              (step === 0 && !canLeaveStep0) ||
              (step === 1 && !canLeaveStep1) ||
              (step === 2 && !canLeaveStep2)
            }
            onClick={() => setStep((s) => (s + 1) as WizardStep)}
          >
            {t('common.next')}
          </Button>
        )}
        {step === 3 && (
          <Button
            block
            color="primary"
            loading={submitting}
            disabled={!evaluation.canDeclare || !canLeaveStep2 || submitting}
            onClick={() => {
              if (evaluation.severityResult === 'pass_with_notes') {
                Dialog.show({
                  title: t('inspection.submitWithNotesTitle'),
                  content: t('inspection.submitWithNotesBody'),
                  closeOnAction: true,
                  actions: [
                    [
                      { key: 'cancel', text: t('common.cancel') },
                      { key: 'ok', text: t('common.submit'), bold: true, onClick: () => { void handleSubmit(); } },
                    ],
                  ],
                });
                return;
              }
              void handleSubmit();
            }}
          >
            {t('common.submit')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default PreTripInspection;
